"use client";

import Link from "next/link";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { H6, Paragraph } from "src/lib/components/salify-next-layout/default/components/Typography";
import { FlexBox } from "src/lib/components/salify-next-layout/default/components/flex-box";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import YouTubeIcon from "@mui/icons-material/YouTube";
import PinterestIcon from "@mui/icons-material/Pinterest";
import InstagramIcon from "@mui/icons-material/Instagram";
import React from "react";
import SalifyFallback from "src/lib/components/salify-fallback/default";
import { layoutConstant } from "src/lib/utils/constants";
import { toAssetImages } from "src/lib/utils";
import Image from "next/image";
import SalifyLink from "src/lib/components/link/salify-link";

export interface VariantProps {
  logoUrl: string;
  logoWidth: number;
  logoHeight: number;
  companyInfo?: Array<{ label: string; value: string }>;
  aboutUs: Array<{ label: string; url: string }>;
  customerCare: Array<{ label: string; url: string }>;
  contactUs: {
    address: string;
    email: string;
    phone: string;
  };
  socialItems: Array<{
    type: "facebook" | "twitter" | "youtube" | "pinterest" | "instagram";
    url: string;
  }>;
}

const ICON_MAPPING: {
  [key in "facebook" | "twitter" | "youtube" | "pinterest" | "instagram"]: any;
} = {
  facebook: FacebookIcon,
  twitter: TwitterIcon,
  youtube: YouTubeIcon,
  pinterest: PinterestIcon,
  instagram: InstagramIcon,
};

const FooterFootmart: React.FC<VariantProps> = (props) => {
  const SocialLinks = () => {
    if (!props.socialItems.length) {
      return null;
    }
    return (
      <FlexBox className="flex" mx={-0.625}>
        {props.socialItems.map(({ type, url }, ind) => {
          const Icon = (ICON_MAPPING as any)[type];
          return Icon ? (
            <SalifyLink href={url} key={ind}>
              <Icon
                sx={{
                  fontSize: 32,
                }}
              />
            </SalifyLink>
          ) : null;
        })}
      </FlexBox>
    );
  };

  return (
    <Box
      component="footer"
      bgcolor="white"
      mb={{
        md: 0,
        margin: 20,
        xs: `${layoutConstant.mobileNavHeight}px`,
      }}
    >
      <Box
        component={Container}
        disableGutters
        pt={{
          sm: 8,
          xs: 4,
        }}
        pb={{
          sm: 2,
        }}
        overflow="hidden"
      >
        <Grid container spacing={3}>
          <Grid
            item
            lg={3}
            md={6}
            sm={6}
            xs={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", sm: "flex-start" },
            }}
          >
            {props.logoUrl && (
              <Link href="/">
                <Image
                  src={toAssetImages(props.logoUrl)}
                  alt=""
                  width={props.logoWidth ?? 160}
                  height={props.logoHeight ?? 48}
                />
              </Link>
            )}
            <br />
          </Grid>

          <Grid item lg={2} md={6} sm={6} xs={12}>
            <H6 fontSize={18} mb={1.5} lineHeight="1">
              <SalifyFallback.Label path="aboutUs" />
            </H6>

            {props.aboutUs.map((item, ind) => (
              <Paragraph key={ind}>
                <SalifyLink href={item.url}>{item.label}</SalifyLink>
              </Paragraph>
            ))}
          </Grid>

          <Grid item lg={2} md={4} sm={4} xs={12}>
            <H6 fontSize={18} mb={1.5} lineHeight="1">
              <SalifyFallback.Label path="customerCare" />
            </H6>

            {props.customerCare.map((item, ind) => (
              <Paragraph key={ind}>
                <SalifyLink href={item.url}>{item.label}</SalifyLink>
              </Paragraph>
            ))}
          </Grid>

          <Grid item lg={4} md={8} sm={8} xs={12}>
            <H6 fontSize={18} mb={1.5} lineHeight="1">
              <SalifyFallback.Label path="contactUs" />
            </H6>

            {props.companyInfo &&
              props.companyInfo.map((item, ind) => (
                <Paragraph key={ind} py={0.6}>
                  <b>{item.label}:</b> {item.value}
                </Paragraph>
              ))}

            <Paragraph py={0.6}>{props.contactUs.address}</Paragraph>

            <Paragraph py={0.6}>
              <b>Email:</b>&nbsp;{props.contactUs.email}
            </Paragraph>

            <Paragraph py={0.6} mb={2}>
              <b>Phone:</b>&nbsp;{props.contactUs.phone}
            </Paragraph>

            <SocialLinks />
          </Grid>
        </Grid>
      </Box>

      <Box component={Container} color="grey.500" pb={{ sm: 4 }}>
        <Grid container spacing={3} alignItems="center" py={1}>
          <Grid item sm={8} xs={12} textAlign={{ xs: "center", sm: "left" }}>
            <SalifyFallback.Label
              path="copyright"
              default="© Simplify Technology 2023. All Rights Reserved."
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default FooterFootmart;
